/*
  Online Garden Co. brand sections (ogc-brand.js). No dependencies.

  1. Video loops ([data-ogc-video]): the file is only fetched when the video is
     on screen, plays muted, pauses off screen, and is never fetched under
     prefers-reduced-motion, data saver or a 2g connection (the poster stays and
     the toggle offers "Play video"). The toggle button is the pause control.
  2. Reveal on scroll (.ogc-reveal): adds .is-visible once, when in view.

  Loaded by several sections; the guard keeps it to one run per page.
*/
(function () {
  if (window.ogcBrand) return;
  window.ogcBrand = { version: 1 };

  var doc = document.documentElement;
  doc.classList.add('ogc-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var smallScreen = window.matchMedia('(max-width: 749px)');
  var connection = navigator.connection || {};
  var lowData = connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');
  var canObserve = 'IntersectionObserver' in window;

  /* Reveal ------------------------------------------------------------- */

  var revealObserver = null;
  if (canObserve) {
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.1 }
    );
  }

  function initReveal(root) {
    var items = root.querySelectorAll('.ogc-reveal:not(.is-visible)');
    items.forEach(function (el) {
      // Phones: no fade-in (finding 4.22, 2026-10-10); fast scrolling showed blank blocks.
      if (!revealObserver || reduceMotion.matches || smallScreen.matches) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* Video loops -------------------------------------------------------- */

  function sourceFor(video) {
    var mobile = smallScreen.matches;
    return (mobile && video.dataset.srcMobile) || video.dataset.src || video.dataset.srcMobile || '';
  }

  function initVideo(video) {
    if (video.dataset.ogcReady) return;
    video.dataset.ogcReady = 'true';

    var wrap = video.closest('[data-ogc-video-wrap]') || video.parentElement;
    var toggle = wrap ? wrap.querySelector('[data-ogc-video-toggle]') : null;
    var src = sourceFor(video);
    if (!src) return;

    var loaded = false;
    var userPaused = false;
    var blocked = false;
    var inView = false;
    var autoplay = !(reduceMotion.matches || lowData);

    function load() {
      if (loaded) return;
      loaded = true;
      video.src = src;
      video.load();
    }

    function play() {
      load();
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function (error) {
          // Autoplay refused (iPhone Low Power Mode): leave the poster and the Play button.
          if (error && error.name === 'NotAllowedError') blocked = true;
        });
      }
    }

    function syncToggle() {
      if (!toggle) return;
      var paused = video.paused;
      toggle.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    }

    function shouldRun() {
      return autoplay && !userPaused && !blocked && (!canObserve || inView) && document.visibilityState !== 'hidden';
    }

    function restart() {
      video.currentTime = 0;
      play();
    }

    video.addEventListener('playing', function () {
      if (wrap) wrap.classList.add('is-playing');
    });
    video.addEventListener('play', syncToggle);
    video.addEventListener('pause', syncToggle);
    // Loop by hand as well as with the loop attribute: Safari and some phones stop
    // on the last frame, or pause a muted video by themselves, and never resume.
    video.addEventListener('ended', function () {
      if (shouldRun()) restart();
    });
    video.addEventListener('timeupdate', function () {
      if (video.duration && video.currentTime >= video.duration - 0.12 && shouldRun()) restart();
    });
    video.addEventListener('pause', function () {
      if (!shouldRun()) return;
      setTimeout(function () {
        if (video.paused && shouldRun()) (video.ended ? restart : play)();
      }, 400);
    });
    document.addEventListener('visibilitychange', function () {
      if (loaded && video.paused && shouldRun()) play();
    });
    // Watchdog: a video on screen that has stopped moving is restarted.
    var lastTime = -1;
    setInterval(function () {
      if (!loaded || !shouldRun()) return;
      if (video.paused || video.ended || video.currentTime === lastTime) {
        if (video.ended || (video.duration && video.currentTime >= video.duration - 0.5)) restart();
        else play();
      }
      lastTime = video.currentTime;
    }, 2000);

    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', function () {
        if (video.paused) {
          userPaused = false;
          blocked = false;
          play();
        } else {
          userPaused = true;
          video.pause();
        }
      });
      syncToggle();
    }

    if (!canObserve) {
      if (autoplay) play();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        inView = entries[0].isIntersecting;
        if (inView && autoplay && !userPaused) {
          play();
        } else if (!inView && loaded && !video.paused) {
          video.pause();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(video);
  }

  /* "Which size?" link (finding 4.15) ----------------------------------- */
  // Jumps to the size guide in the product description ("Which size?" in bold); without one, the
  // link's own #ogc-size-guide anchor (the "Works great on" block) is used.

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-ogc-size-link]');
    if (!link) return;
    var section = link.closest('.shopify-section') || document;
    var target = null;
    section.querySelectorAll('.rte strong, .rte b, rte-formatter strong').forEach(function (el) {
      if (!target && /^\s*which size/i.test(el.textContent || '')) target = el.closest('p, li') || el;
    });
    if (!target) return;
    event.preventDefault();
    var rect = target.getBoundingClientRect();
    if (rect.top < 80 || rect.bottom > window.innerHeight - 100) {
      window.scrollTo({
        top: Math.max(0, rect.top + window.scrollY - window.innerHeight / 3),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }
    target.classList.add('ogc-flash');
    setTimeout(function () {
      target.classList.remove('ogc-flash');
    }, 1600);
  });

  /* Cart add-on buttons (finding 4.14) ---------------------------------- */
  // Same handler as the inline one in snippets/ogc-free-shipping.liquid, for a drawer that was
  // empty at page load (its inline script never ran).

  if (!window.ogcAddonBound) {
    window.ogcAddonBound = true;
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-ogc-addon]');
      if (!link) return;
      var update = window.Shopify && window.Shopify.actions && window.Shopify.actions.updateCart;
      if (typeof update !== 'function') return;
      event.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      var lines = link
        .getAttribute('data-ogc-addon')
        .split(',')
        .filter(Boolean)
        .map(function (id) {
          return { merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 };
        });
      update({ lines: lines })
        .then(function (result) {
          if (result && result.userErrors && result.userErrors.length) window.location.href = link.href;
        })
        .catch(function () {
          window.location.href = link.href;
        })
        .finally(function () {
          link.removeAttribute('aria-busy');
        });
    });
  }

  /* Boot --------------------------------------------------------------- */

  function init(root) {
    root = root || document;
    initReveal(root);
    root.querySelectorAll('video[data-ogc-video]').forEach(initVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
